import React, { useState, type RefObject } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import { Heading5, Label, Variant } from '~/components/atom/Typography';
import ResponsivePopup from '~/components/molecule/ResponsivePopup';
import Textarea from '~/components/molecule/Textarea';
import Button from '~/components/atom/Button';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import withIconTooltip from '~/hocs/withIconTooltip';

const text = {
  locationSettings: 'Locatie instellingen',
  noteForTheAgent: 'Notitie voor de agent',
  preferredTooltip: 'De voorkeur wordt getoond aan de call-agent',
  notePlaceholder:
    'Voeg een notitie toe die zichtbaar is voor de call center agent...',
  cancel: 'Annuleren',
  save: 'Opslaan',
  preferred: 'Voorkeurslocatie',
};

type Props = {
  openerRef: RefObject<HTMLElement>;
  note: string;
  weight: number;
  onSave: (note: string, weight: number) => void;
  onClose: () => void;
};

const LocationSettingsPopupV2: React.FC<Props> = ({
  openerRef,
  note: initialNote,
  weight: initialWeight,
  onSave,
  onClose,
}) => {
  const [note, setNote] = useState(initialNote);
  const [weight, setWeight] = useState(initialWeight);

  return (
    <ResponsivePopup
      isVisible
      openerRef={openerRef}
      rootId="location-settings"
      onClickOutside={onClose}
    >
      <JustificationContainer
        backgroundColor={{ group: 'white' }}
        padding={['m']}
        direction="column"
        gap="m"
        boxShadow="base"
        border={{ radius: 'm' }}
      >
        <Heading5 margin={[null]}>{text.locationSettings}</Heading5>

        <JustificationContainer direction="column" gap="s" width="100%">
          <ToggleCheckbox
            label={
              <LabelWithIcon
                tooltipMessage={text.preferredTooltip}
                width="100%"
                margin={[null]}
              >
                {text.preferred}
              </LabelWithIcon>
            }
            size="small"
            value={weight > 0}
            onChange={() => setWeight(weight > 0 ? 0 : 10)}
          />
          <JustificationContainer direction="column" gap="xxxs">
            <JustificationContainer
              align="center"
              justification="space-between"
              width="100%"
            >
              <Label margin={[null]} size="s" variant={Variant.primary}>
                {text.noteForTheAgent}
              </Label>
              <TextButton
                icon="delete"
                appearance="danger"
                padding={[null]}
                onClick={() => setNote('')}
              />
            </JustificationContainer>
            <Textarea
              name="location-note-v2"
              value={note}
              onChange={value => setNote(value || '')}
              placeholder={text.notePlaceholder}
            />
          </JustificationContainer>
        </JustificationContainer>

        <JustificationContainer
          width="100%"
          justification="end"
          gap="s"
          align="center"
        >
          <TextButton
            label={text.cancel}
            onClick={onClose}
            appearance="danger"
          />
          <Button
            appearance="secondary"
            label={text.save}
            onClick={() => {
              onSave(note, weight);
              onClose();
            }}
          />
        </JustificationContainer>
      </JustificationContainer>
    </ResponsivePopup>
  );
};

const LabelWithIcon = withIconTooltip(Label);

export default LocationSettingsPopupV2;
